import { useTu } from "@src/appV2/i18n/useTu";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Divider, Stack } from "@mui/material";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { DollarAvatar } from "../../ShiftDetails/DollarAvatar";
import { ShiftDetailsChargeRateSkeleton } from "../../ShiftDetails/ShiftDetailsChargeRateSkeleton";
import { ShiftDetailsInfoRow } from "../../ShiftDetails/ShiftDetailsInfoRow";

interface InviteChargeRateRowProps {
  chargeRateDisplay: string;
  baseChargeRateDisplay?: string;
  boostRateDisplay?: string;
  isChargeRateLoading: boolean;
  onEditChargeRate?: () => void;
}

export function InviteChargeRateRow(props: InviteChargeRateRowProps) {
  const { tu } = useTu("schedule");

  const {
    chargeRateDisplay,
    baseChargeRateDisplay,
    boostRateDisplay,
    isChargeRateLoading,
    onEditChargeRate,
  } = props;

  if (isChargeRateLoading && !chargeRateDisplay) {
    return <ShiftDetailsChargeRateSkeleton />;
  }

  return (
    <ShiftDetailsInfoRow
      avatar={<DollarAvatar />}
      primaryText={chargeRateDisplay}
      secondaryText="Charge Rate"
      actionButton={
        isDefined(onEditChargeRate) ? (
          <IconButton
            aria-label={tu("Edit charge rate")}
            iconType="pencil"
            size="small"
            variant="outlined"
            onClick={(event) => {
              event.stopPropagation();
              onEditChargeRate();
            }}
          />
        ) : undefined
      }
      expandableContent={
        isDefined(baseChargeRateDisplay) ? (
          <Stack sx={{ gap: 2 }}>
            <Divider />
            <Box
              sx={{
                display: "flex",
                paddingX: 4,
                paddingY: 2,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text variant="caption" sx={(theme) => ({ color: theme.palette.base?.inactive })}>
                {tu("Base rate")}
              </Text>
              <Text
                semibold
                variant="caption"
                sx={(theme) => ({ color: theme.palette.base?.base })}
              >
                {baseChargeRateDisplay}
              </Text>
            </Box>
            {isDefined(boostRateDisplay) && (
              <>
                <Divider />
                <Box
                  sx={{
                    display: "flex",
                    paddingX: 4,
                    paddingY: 2,
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text variant="caption" sx={(theme) => ({ color: theme.palette.base?.inactive })}>
                    {tu("Boost rate")}
                  </Text>
                  <Text
                    semibold
                    variant="caption"
                    sx={(theme) => ({ color: theme.palette.base?.base })}
                  >
                    {boostRateDisplay}
                  </Text>
                </Box>
              </>
            )}
          </Stack>
        ) : undefined
      }
    />
  );
}
